import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Installment {
  id: string;
  name: string;
  amount: number;
  dueDate: string;
  monthDue: string;
}

interface FeeStructureData {
  id: string;
  className: string;
  academicYear: string;
  totalAmount: number;
  installments: Installment[];
  feeComponents: {
    tuitionFee: number;
    admissionFee: number;
    examFee: number;
    libraryFee: number;
    sportsFee: number;
    labFee: number;
    transportFee: number;
    otherFees: number;
  };
  createdAt: string;
}

interface FeeStructureProps {
  domain: string;
}

const FeeStructure: React.FC<FeeStructureProps> = ({ domain }) => {
  const [feeStructures, setFeeStructures] = useState<FeeStructureData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [className, setClassName] = useState('');
  const [academicYear, setAcademicYear] = useState('2024-2025');
  const [feeComponents, setFeeComponents] = useState({
    tuitionFee: 0,
    admissionFee: 0,
    examFee: 0,
    libraryFee: 0,
    sportsFee: 0,
    labFee: 0,
    transportFee: 0,
    otherFees: 0,
  });
  const [installments, setInstallments] = useState<Omit<Installment, 'id'>[]>([
    { name: 'First Installment', amount: 0, dueDate: '', monthDue: 'April' },
  ]);

  useEffect(() => {
    if (domain) {
      loadFeeStructures();
    }
  }, [domain]);

  const loadFeeStructures = async () => {
    setLoading(true);
    try {
      const feeStructuresRef = collection(db, `school-users/${domain}/fee-structures`);
      const snapshot = await getDocs(feeStructuresRef);

      const structures: FeeStructureData[] = [];
      snapshot.forEach((doc) => {
        structures.push({ id: doc.id, ...doc.data() } as FeeStructureData);
      });

      setFeeStructures(structures.sort((a, b) => a.className.localeCompare(b.className)));
    } catch (error) {
      console.error('Error loading fee structures:', error);
      alert('Failed to load fee structures');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalAmount = () => {
    return Object.values(feeComponents).reduce((sum, val) => sum + Number(val), 0);
  };

  const handleFeeComponentChange = (component: keyof typeof feeComponents, value: string) => {
    setFeeComponents(prev => ({
      ...prev,
      [component]: Number(value) || 0
    }));
  };

  const handleInstallmentChange = (index: number, field: keyof Omit<Installment, 'id'>, value: string | number) => {
    setInstallments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: field === 'amount' ? Number(value) : value };
      return updated;
    });
  };

  const addInstallment = () => {
    setInstallments(prev => [
      ...prev,
      { name: `Installment ${prev.length + 1}`, amount: 0, dueDate: '', monthDue: '' }
    ]);
  };

  const removeInstallment = (index: number) => {
    if (installments.length > 1) {
      setInstallments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const distributeAmountEqually = () => {
    const total = calculateTotalAmount();
    const perInstallment = Math.floor(total / installments.length);
    const remainder = total % installments.length;

    setInstallments(prev => prev.map((inst, idx) => ({
      ...inst,
      amount: perInstallment + (idx === 0 ? remainder : 0)
    })));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!className.trim()) {
      alert('Please enter a class name');
      return;
    }

    const totalAmount = calculateTotalAmount();
    const installmentTotal = installments.reduce((sum, inst) => sum + inst.amount, 0);

    if (installmentTotal !== totalAmount) {
      alert(`Installment amounts (${installmentTotal}) must equal total fee amount (${totalAmount})`);
      return;
    }

    setLoading(true);
    try {
      const feeStructureData = {
        className: className.trim(),
        academicYear,
        totalAmount,
        feeComponents,
        installments: installments.map((inst, idx) => ({
          id: `inst_${idx + 1}`,
          ...inst
        })),
        createdAt: editingId ? feeStructures.find(f => f.id === editingId)?.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingId) {
        const feeStructureRef = doc(db, `school-users/${domain}/fee-structures`, editingId);
        await updateDoc(feeStructureRef, feeStructureData);
        alert('Fee structure updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/fee-structures`), feeStructureData);
        alert('Fee structure created successfully!');
      }

      resetForm();
      loadFeeStructures();
    } catch (error) {
      console.error('Error saving fee structure:', error);
      alert('Failed to save fee structure');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (structure: FeeStructureData) => {
    setEditingId(structure.id);
    setClassName(structure.className);
    setAcademicYear(structure.academicYear);
    setFeeComponents(structure.feeComponents);
    setInstallments(structure.installments.map(({ id, ...rest }) => rest));
    setShowForm(true);
  };

  const handleDelete = async (id: string, className: string) => {
    if (!confirm(`Are you sure you want to delete the fee structure for ${className}?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(db, `school-users/${domain}/fee-structures`, id));
      alert('Fee structure deleted successfully!');
      loadFeeStructures();
    } catch (error) {
      console.error('Error deleting fee structure:', error);
      alert('Failed to delete fee structure');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setClassName('');
    setAcademicYear('2024-2025');
    setFeeComponents({
      tuitionFee: 0,
      admissionFee: 0,
      examFee: 0,
      libraryFee: 0,
      sportsFee: 0,
      labFee: 0,
      transportFee: 0,
      otherFees: 0,
    });
    setInstallments([{ name: 'First Installment', amount: 0, dueDate: '', monthDue: 'April' }]);
    setShowForm(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-receipt me-2 text-primary-custom"></i>
              Fee Structure Management
            </h5>
            <small className="text-muted">Configure fee structures for each class with installments</small>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
            disabled={loading}
          >
            <i className={`bi ${showForm ? 'bi-x-lg' : 'bi-plus-lg'} me-2`}></i>
            {showForm ? 'Cancel' : 'Add Fee Structure'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {showForm && (
          <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">
                {editingId ? 'Edit Fee Structure' : 'Create New Fee Structure'}
              </h6>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Class Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 10-A, 9-B"
                      value={className}
                      onChange={(e) => setClassName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Academic Year *</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 2024-2025"
                      value={academicYear}
                      onChange={(e) => setAcademicYear(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <h6 className="fw-bold mb-3 text-primary">Fee Components</h6>
                <div className="row g-3 mb-4">
                  {Object.keys(feeComponents).map((component) => (
                    <div key={component} className="col-md-3">
                      <label className="form-label text-capitalize">
                        {component.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={feeComponents[component as keyof typeof feeComponents]}
                        onChange={(e) => handleFeeComponentChange(component as keyof typeof feeComponents, e.target.value)}
                        min="0"
                      />
                    </div>
                  ))}
                </div>

                <div className="alert alert-info">
                  <strong>Total Fee Amount:</strong> {formatCurrency(calculateTotalAmount())}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0 text-primary">Installment Plan</h6>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={distributeAmountEqually}
                    >
                      <i className="bi bi-distribute-vertical me-1"></i>
                      Distribute Equally
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={addInstallment}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Add Installment
                    </button>
                  </div>
                </div>

                {installments.map((installment, index) => (
                  <div key={index} className="card mb-3 border">
                    <div className="card-body">
                      <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                          <label className="form-label">Installment Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={installment.name}
                            onChange={(e) => handleInstallmentChange(index, 'name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Amount (₹)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={installment.amount}
                            onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                            min="0"
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Due Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={installment.dueDate}
                            onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Month Due</label>
                          <select
                            className="form-select"
                            value={installment.monthDue}
                            onChange={(e) => handleInstallmentChange(index, 'monthDue', e.target.value)}
                            required
                          >
                            <option value="">Select Month</option>
                            {['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'].map(month => (
                              <option key={month} value={month}>{month}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-1">
                          {installments.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm w-100"
                              onClick={() => removeInstallment(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : editingId ? 'Update Fee Structure' : 'Create Fee Structure'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Existing Fee Structures List */}
        {loading && !showForm ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : feeStructures.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-receipt fs-1 d-block mb-3"></i>
            <p>No fee structures created yet. Click "Add Fee Structure" to create one.</p>
          </div>
        ) : (
          <div className="row g-3">
            {feeStructures.map((structure) => (
              <div key={structure.id} className="col-md-6">
                <div className="card h-100 border">
                  <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold">{structure.className}</h6>
                      <span className="badge bg-primary">{structure.academicYear}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <strong>Total Amount:</strong>
                        <span className="text-success fw-bold">{formatCurrency(structure.totalAmount)}</span>
                      </div>
                    </div>

                    <h6 className="text-muted small mb-2">Installments ({structure.installments.length}):</h6>
                    <div className="list-group list-group-flush">
                      {structure.installments.map((inst) => (
                        <div key={inst.id} className="list-group-item px-0 py-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <small className="fw-semibold">{inst.name}</small>
                              <br />
                              <small className="text-muted">{inst.monthDue} - {inst.dueDate}</small>
                            </div>
                            <span className="badge bg-success">{formatCurrency(inst.amount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="card-footer bg-white">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary flex-grow-1"
                        onClick={() => handleEdit(structure)}
                        disabled={loading}
                      >
                        <i className="bi bi-pencil me-1"></i>Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(structure.id, structure.className)}
                        disabled={loading}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeeStructure;
